import { useCallback, useMemo, useState } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import EnvironmentGuard from 'src/components/EnvironmentGuard';
import ViewByToggle from 'src/components/ViewByToggle';
import DistributionBarChart, {
  DISTRIBUTION_PAGE_SIZE,
} from 'src/features/charts/DistributionBarChart';
import { readFilterParams, useFilterParams } from 'src/hooks/filterParams';
import { useInfiniteDashboardPolicies } from 'src/hooks/dashboard';
import { PORTAL_ROUTE } from 'src/constants/routes';
import { useCanViewBasePolicyGroups } from 'src/utils/featureFlags';
import DetectedPoliciesTable from './DetectedPoliciesTable';
import BasePolicyGroupsTable from './BasePolicyGroupsTable';

const SECTION_TITLE_SX = {
  color: '#FFFFFF',
  fontWeight: 700,
  fontSize: '1.05rem',
  lineHeight: 1.4,
};

// Hoisted because the chart is memoized.
const FULL_WIDTH_CARD_SX = { width: '100%' };

// Which table is showing is part of the view rather than of either table's filters, so the page
// owns it and keeps it in the URL next to them.
const VIEW_OPTIONS = [
  { value: 'list', label: 'List', icon: 'mdi:format-list-bulleted' },
  { value: 'group', label: 'Group', icon: 'mdi:file-tree-outline' },
];
const VIEWS = VIEW_OPTIONS.map(option => option.value);
const DEFAULT_VIEW = { view: VIEWS[0] };

const SECTION_TITLE_BY_VIEW = {
  list: 'Detected Policies',
  group: 'Base Policy Groups',
};

const DetectedPolicies = () => {
  const navigate = useNavigate();
  const canViewGroups = useCanViewBasePolicyGroups();

  // The toggle is kept even when it has a single option, so the page reads the same whether or not
  // grouping is enabled and gains the second button without moving anything.
  const viewOptions = useMemo(
    () => VIEW_OPTIONS.filter(option => canViewGroups || option.value === DEFAULT_VIEW.view),
    [canViewGroups],
  );

  // A URL can still carry the group view from a session that was allowed to see it, so the
  // permission decides which table renders rather than only which toggle is offered.
  const [storedView, setStoredView] = useState(() => {
    const stored = readFilterParams(DEFAULT_VIEW) ?? DEFAULT_VIEW;
    return VIEWS.includes(stored.view) ? stored.view : DEFAULT_VIEW.view;
  });
  const activeView = canViewGroups ? storedView : DEFAULT_VIEW.view;
  const viewParams = useMemo(() => ({ view: activeView }), [activeView]);

  useFilterParams(viewParams);

  const handleOpenPolicy = useCallback(
    envPolicyId => navigate(`${PORTAL_ROUTE}/policy-details/${envPolicyId}/general`),
    [navigate],
  );

  // The overview is environment-wide: it always shows visible, active base policies and does not
  // follow the filters of whichever table is showing.
  const distributionQuery = useInfiniteDashboardPolicies({ limit: DISTRIBUTION_PAGE_SIZE });

  const distributionData = useMemo(
    () =>
      distributionQuery.data?.pages?.flatMap(page =>
        (page.items || []).map(policy => ({
          id: policy.policyId,
          label: policy.policyName,
          value: policy.percentage,
          deviceCount: policy.devicesWithPolicy,
        })),
      ) || [],
    [distributionQuery.data],
  );

  const loadMoreDistribution = useCallback(() => {
    if (distributionQuery.hasNextPage) {
      distributionQuery.fetchNextPage();
    }
  }, [distributionQuery]);

  return (
    <EnvironmentGuard>
      <Stack gap={2.5}>
        <Stack gap={1.5}>
          <Typography sx={SECTION_TITLE_SX}>Policies Overview</Typography>
          <DistributionBarChart
            dense
            titleSize="sm"
            title="Device Policy Distribution"
            subtitle="Visible, active base policies · click for policy details"
            data={distributionData}
            total={distributionQuery.data?.pages?.[0]?.total}
            isLoading={distributionQuery.isLoading}
            isLoadingMore={distributionQuery.isFetchingNextPage}
            hasMore={!!distributionQuery.hasNextPage}
            onLoadMore={loadMoreDistribution}
            onItemClick={item => handleOpenPolicy(item.id)}
            emptyMessage="No base policies are applied to devices yet."
            sx={FULL_WIDTH_CARD_SX}
          />
        </Stack>

        <Box>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            gap={1}
            sx={{ mb: 1 }}>
            <Typography sx={SECTION_TITLE_SX}>{SECTION_TITLE_BY_VIEW[activeView]}</Typography>
            <ViewByToggle value={activeView} options={viewOptions} onChange={setStoredView} />
          </Stack>

          {activeView === 'group' ? <BasePolicyGroupsTable /> : <DetectedPoliciesTable />}
        </Box>
      </Stack>
    </EnvironmentGuard>
  );
};

export default DetectedPolicies;
